// @flow
import React from 'react';
import classnames from 'classnames';
import { translate, type TFunction } from 'react-i18next';
import { Flex } from '@locus-taxy/neo-ui';
import { IconWithPopover } from 'common/components/core/icon';
import DateTimePicker from 'common/components/core/form/DateTimePicker';
import TimeUtils, { DateTimeFormat } from 'common/utils/TimeUtils';
import { neoUIColors } from 'common/enums/ColorEnum';
import {
    PopoverTriggerEnum,
    PopoverPlaceEnum
} from 'common/components/core/popover/popOverFactory';
import type Moment from 'moment-timezone';
import theme from './DateTimeRangeSelector.scss';
import _isNil from 'lodash/isNil';
import { type LocaleSettings } from 'common/models/internal/LocaleSettings';

type Props = {
    showDate: boolean,
    showTime: boolean,
    t: TFunction,
    value: ?{ min: string, max: string },
    onChange: (?{ min: string, max: string }) => void,
    localeSettings: LocaleSettings
};

type State = {
    fromValue: Moment,
    toValue: Moment,
    error: ?string
};

const ErrorTypes = {
    ToEqualToFrom: 'TO_EQUALTO_FROM',
    FromLaterThanTo: 'FROM_LATER_THAN_TO'
};

const FieldType = {
    From: 'FROM',
    To: 'TO'
};

const ErrorIcon = ({ popOverContent }) => {
    return (
        <IconWithPopover
            className={theme.errorIcon}
            theme={theme}
            icon="alert-new"
            popOverContent={popOverContent}
            triggerPopoverOn={PopoverTriggerEnum.Hover}
            preferPlace={PopoverPlaceEnum.Above}
            iconColor={neoUIColors.red500.name}
        />
    );
};

const fieldProps = {
    timeFormat: DateTimeFormat.HUMAN_TIME_FORMAT,
    timePlaceholder: 'HH:MM',
    timeStep: 15,
    showDateTimeSeparator: true,
    ErrorIcon
};
class DateTimeRangeSelector extends React.PureComponent<Props, State> {
    static defaultProps = {
        showDate: true,
        showTime: true
    };

    constructor(props) {
        super(props);
        const { value } = props;
        this.state = {
            fromValue: value ? TimeUtils.getMoment(value.min) : null,
            toValue: value ? TimeUtils.getMoment(value.max) : null,
            error: null
        };
    }

    componentDidUpdate(prevProps, prevState) {
        let { toValue, fromValue } = this.state;
        const { toValue: prevToValue, fromValue: prevFromValue } = prevState;
        const { showTime } = this.props;
        let error = null;

        if (toValue !== prevToValue || fromValue !== prevFromValue) {
            if (!showTime) {
                toValue = this.getFormattedValue(toValue);
                fromValue = this.getFormattedValue(fromValue);
            }
            if (showTime && TimeUtils.isSame(fromValue, toValue)) {
                error = ErrorTypes.ToEqualToFrom;
            } else if (TimeUtils.isAfter(fromValue, toValue)) {
                error = ErrorTypes.FromLaterThanTo;
            }
            this.setState({ error }, this.updateParent);
        }

        // This is when value is cleared from filter form's clear button
        if (_isNil(this.props.value) && prevProps.value) {
            this.setState({ fromValue: null, toValue: null, error: null });
        }
    }

    getFormattedValue = value => {
        if (!value) {
            return value;
        }
        const { showTime } = this.props;
        if (showTime) {
            return TimeUtils.getUtcTimeWithMillis(value);
        }
        return TimeUtils.format(value, DateTimeFormat.LONG_DATE_FORMAT);
    };

    updateParent = () => {
        const { fromValue, toValue, error } = this.state;
        const min = this.getFormattedValue(fromValue);
        const max = this.getFormattedValue(toValue);
        this.props.onChange(
            {
                ...(min && { min }),
                ...(max && { max })
            },
            Boolean(error)
        );
    };

    onFromChange = value => {
        this.setState({ fromValue: value });
    };

    onToChange = value => {
        this.setState({ toValue: value });
    };

    getErrorMessage = fieldType => {
        const { t } = this.props;
        const { error } = this.state;
        // Show for both from and to fields
        if (error === ErrorTypes.ToEqualToFrom) {
            return t('validations:minEqualToMax');
        }
        // Show when from is selected and user selects to earlier than from
        if (
            error === ErrorTypes.FromLaterThanTo &&
            fieldType === FieldType.To
        ) {
            return t('validations:time.toEarlierThanFrom');
        }
        // Show when to is selected and user selects from later than to
        if (
            error === ErrorTypes.FromLaterThanTo &&
            fieldType === FieldType.From
        ) {
            return t('validations:time.fromLaterThanTo');
        }

        return null;
    };

    onFromClear = () => {
        this.onFromChange(null);
    };
    onToClear = () => {
        this.onToChange(null);
    };

    render() {
        const { showDate, field, showTime, t, localeSettings } = this.props;
        const { fromValue, toValue } = this.state;
        const isDateAndTimeEnabled = showDate && showTime;
        let isDateFormatMMDDYYYY = false;
        if (localeSettings) {
            isDateFormatMMDDYYYY = localeSettings.isDateFormatMMDDYYYY();
        }
        const dateFormat = isDateFormatMMDDYYYY
            ? DateTimeFormat.SHEET_DATE_FORMAT_MONTH_FIRST
            : DateTimeFormat.SHEET_DATE_FORMAT;
        const commonProps = {
            ...fieldProps,
            showDate,
            showTime,
            dateFormat,
            datePlaceholder: dateFormat
        };
        return (
            <Flex
                className={theme.dateTimeRangeSelector}
                flexDirection={
                    isDateAndTimeEnabled
                        ? Flex.Direction.Column
                        : Flex.Direction.Row
                }
                alignItems={
                    isDateAndTimeEnabled
                        ? Flex.AlignItems.flexStart
                        : Flex.AlignItems.Center
                }
            >
                <DateTimePicker
                    {...commonProps}
                    testId={`${field.headerTextId}FilterFrom`}
                    value={fromValue}
                    error={this.getErrorMessage(FieldType.From)}
                    onChange={this.onFromChange}
                    theme={theme}
                    dateLabel={t('fromDate')}
                    timeLabel={t('fromTime')}
                    onClearInput={this.onFromClear}
                    isClearable
                />
                {!isDateAndTimeEnabled && <div className={theme.separator} />}
                <DateTimePicker
                    {...commonProps}
                    testId={`${field.headerTextId}FilterTo`}
                    className={classnames({
                        [theme.isDateAndTimeEnabled]: isDateAndTimeEnabled
                    })}
                    error={this.getErrorMessage(FieldType.To)}
                    value={toValue}
                    onChange={this.onToChange}
                    theme={theme}
                    dateLabel={t('toDate')}
                    timeLabel={t('toTime')}
                    onClearInput={this.onToClear}
                    isClearable
                />
            </Flex>
        );
    }
}
const TranslatedDateTimeRangeSelector = translate()(DateTimeRangeSelector);
const DateRangeSelector = (props: Props) => {
    return <TranslatedDateTimeRangeSelector {...props} showTime={false} />;
};

const TimeRangeSelector = (props: Props) => {
    return <TranslatedDateTimeRangeSelector {...props} showDate={false} />;
};

export default TranslatedDateTimeRangeSelector;
export { TimeRangeSelector, DateRangeSelector };
